
import Vue from "vue"
import Component from "vue-class-component"
import {QItem, QItemSection, QSelect} from "quasar"
import {Prop, Watch} from "vue-property-decorator"
import Axios, {AxiosRequestConfig} from "axios"

@Component({components: {QSelect, QItem, QItemSection}})
export default class SelectDropDown extends Vue {
  private static readonly INITIAL_DISPLAY_LENGTH_PX = 250
  private static readonly MAX_UNADJUSTED_DISPLAY_LENGTH = 20
  private static readonly EXTRA_DISPLAY_CHARACTER_FACTOR_PX = 5
  private static readonly FIXED_EXTRA_ADJUSTMENT_PX = 15

  @Prop()
  label: string

  @Prop({default: "label"})
  optionLabel: ((option: any) => string) | string

  @Prop()
  getOptionsPath: string | null

  @Prop({default: "label"})
  displayValueField: string

  @Prop({default: false})
  disable: boolean

  @Prop({default: false})
  readonly: boolean

  @Prop({default: false})
  dark: boolean

  @Prop({default: 0})
  minimumInputLength: number

  @Prop()
  getOptionsParamName: string | undefined

  @Prop()
  getOptionsParamValue: string | undefined

  @Prop()
  flags: Array<string> | undefined

  @Prop({default: false})
  getOptionsParamRequired: boolean

  @Prop({default: true})
  useInput: boolean

  @Prop({default: true})
  clearable: boolean

  @Prop({default: SelectDropDown.INITIAL_DISPLAY_LENGTH_PX})
  width: number

  @Prop({default: true})
  autoAdjustWidth: boolean

  $refs: {
    select: QSelect
  }

  options = []
  model: any | null = <any>null

  isLoadingOptions = false

  @Watch("getOptionsPath", {immediate: true})
  onGetOptionsPathChanged() {
    this.loadInitialOptions()
  }

  @Watch("getOptionsParamValue", {immediate: true})
  onGetOptionsParamValueChanged() {
    this.loadInitialOptions()
  }

  get style(): string {
    return `font-size: 12px; width: ${this.displayWidth}px;`
  }

  get displayValue(): string {
    return this.model === null
      ? ""
      : typeof this.model == "object"
      ? this.model[this.displayValueField]
      : this.model
  }

  get displayWidth(): number {
    return this.autoAdjustWidth ? this.calculatedDisplayLength() : this.width
  }

  private loadInitialOptions() {
    if (this.minimumInputLength === 0 && this.getOptionsPath != null) {
      this.isLoadingOptions = true
      void this.getOptions("", this.getOptionsPath).then(options => {
        this.options = options
        this.isLoadingOptions = false
      })
    }
  }

  private getRequestConfig(input: string): AxiosRequestConfig {
    let parameters = {name: input}
    if (this.getOptionsParamName != null && this.getOptionsParamValue != null) {
      parameters = {
        ...parameters,
        ...{[this.getOptionsParamName]: this.getOptionsParamValue},
      }
    }

    if (this.flags != null) {
      this.flags.forEach(f => (parameters = {...parameters, ...{[f]: true}}))
    }

    return {params: parameters}
  }

  private async getOptions(input: string, url: string): Promise<[]> {
    return input.length >= this.minimumInputLength &&
      input.length < 15 &&
      (!this.getOptionsParamRequired || this.getOptionsParamValue != null)
      ? (await Axios.get(url, this.getRequestConfig(input))).data
      : this.options
  }

  private async checkInput(input: string) {
    if (this.getOptionsPath != null) {
      this.options = await this.getOptions(input, this.getOptionsPath)
      this.$refs.select.refresh()
      if (input.length > 0 && this.model == null) {
        this.$refs.select.showPopup()
      }
    }
  }

  private calculatedDisplayLength(): number {
    const displayLength = this.displayValue.length
    let adjustment = 0
    if (displayLength > SelectDropDown.MAX_UNADJUSTED_DISPLAY_LENGTH) {
      adjustment =
        (displayLength - SelectDropDown.MAX_UNADJUSTED_DISPLAY_LENGTH) *
          SelectDropDown.EXTRA_DISPLAY_CHARACTER_FACTOR_PX +
        SelectDropDown.FIXED_EXTRA_ADJUSTMENT_PX
    }

    return SelectDropDown.INITIAL_DISPLAY_LENGTH_PX + adjustment
  }

  private input(option: any) {
    this.$emit("input", option)
  }
}
